import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Eye, Clock, Flame, Ticket, 
  Users, BarChart3, Calendar, AlertTriangle, Check 
} from 'lucide-react';
import api from '../../utils/api';

const DropManagement = () => {
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDrop, setSelectedDrop] = useState(null);
  const [stats, setStats] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    dropDate: '',
    endDate: '',
    isLimited: true,
    totalQuantity: 100,
    maxPerCustomer: 2,
    useQueue: false,
    isRaffle: false,
    isCollab: false,
    collaboratorName: '',
    heroImage: '',
    isActive: true
  });

  useEffect(() => {
    fetchDrops();
  }, []);

  const fetchDrops = async () => {
    try {
      const res = await api.get('/drops?limit=50');
      setDrops(res.data.data);
    } catch (error) {
      console.error('Error fetching drops:', error);
    }
  };

  const fetchStats = async (dropId) => {
    try {
      const res = await api.get(`/drops/admin/${dropId}/stats`);
      setStats(res.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (selectedDrop) {
        await api.put(`/drops/admin/${selectedDrop._id}`, formData);
      } else {
        await api.post('/drops/admin', formData);
      }
      
      setShowModal(false);
      setSelectedDrop(null);
      resetForm();
      fetchDrops();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save drop');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this drop?')) return;
    
    try {
      await api.delete(`/drops/admin/${id}`);
      fetchDrops();
    } catch (error) {
      alert('Failed to delete drop');
    }
  };

  const handleEdit = (drop) => {
    setSelectedDrop(drop);
    setFormData({
      name: drop.name,
      slug: drop.slug,
      description: drop.description || '',
      dropDate: new Date(drop.dropDate).toISOString().slice(0, 16),
      endDate: drop.endDate ? new Date(drop.endDate).toISOString().slice(0, 16) : '',
      isLimited: drop.isLimited,
      totalQuantity: drop.totalQuantity || 100,
      maxPerCustomer: drop.maxPerCustomer || 2,
      useQueue: drop.useQueue,
      isRaffle: drop.isRaffle,
      isCollab: drop.isCollab,
      collaboratorName: drop.collaboratorName || '',
      heroImage: drop.heroImage || '',
      isActive: drop.isActive
    });
    fetchStats(drop._id);
    setShowModal(true);
  };

  const handleDrawRaffle = async (dropId) => {
    if (!confirm('Draw raffle winners? This cannot be undone.')) return;
    
    try {
      const res = await api.post(`/drops/admin/${dropId}/raffle/draw`);
      alert(`Winners drawn! ${res.data.data.winnersCount} winners selected.`);
      fetchStats(dropId);
    } catch (error) {
      alert('Failed to draw raffle');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      dropDate: '',
      endDate: '',
      isLimited: true,
      totalQuantity: 100,
      maxPerCustomer: 2,
      useQueue: false,
      isRaffle: false,
      isCollab: false,
      collaboratorName: '',
      heroImage: '',
      isActive: true
    });
    setStats(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-green-500';
      case 'scheduled': return 'bg-yellow-500';
      case 'sold_out': return 'bg-red-500';
      case 'ended': return 'bg-gray-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Drop Management</h1>
          <p className="text-gray-600 font-mono text-sm">Manage hype drops, raffles, and releases</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-yellow-400 text-black px-6 py-3 font-black uppercase tracking-wider border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all flex items-center gap-2"
        >
          <Plus size={20} />
          Create Drop
        </button>
      </div>

      {/* Drops Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {drops.map((drop) => (
          <div 
            key={drop._id} 
            className="bg-white border-4 border-black shadow-[8px_8px_0px_rgba(0,0,0,0.1)] overflow-hidden"
          >
            {/* Image */}
            <div className="relative h-48 bg-gray-100">
              {drop.heroImage ? (
                <img 
                  src={drop.heroImage} 
                  alt={drop.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No Image
                </div>
              )}
              
              {/* Status Badge */}
              <div className={`absolute top-3 left-3 ${getStatusColor(drop.status)} text-white px-3 py-1 font-bold uppercase text-xs border-2 border-white shadow-md`}>
                {drop.status}
              </div>
              
              {/* Type Badges */}
              <div className="absolute top-3 right-3 flex flex-col gap-1">
                {drop.isRaffle && (
                  <div className="bg-purple-500 text-white px-2 py-1 font-bold uppercase text-xs">
                    <Ticket size={12} className="inline mr-1" />
                    Raffle
                  </div>
                )}
                {drop.isLimited && (
                  <div className="bg-orange-500 text-white px-2 py-1 font-bold uppercase text-xs">
                    <Flame size={12} className="inline mr-1" />
                    Limited
                  </div>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <h3 className="font-black text-lg uppercase tracking-tighter mb-1">{drop.name}</h3>
              <p className="text-gray-500 text-xs font-mono mb-3">
                {new Date(drop.dropDate).toLocaleDateString('en-IN', { 
                  weekday: 'short', 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                <div className="bg-gray-100 p-2">
                  <Users size={16} className="mx-auto mb-1 text-gray-600" />
                  <p className="font-black text-sm">{drop.notifyUsers?.length || 0}</p>
                  <p className="text-xs text-gray-500">Waiting</p>
                </div>
                <div className="bg-gray-100 p-2">
                  <Eye size={16} className="mx-auto mb-1 text-gray-600" />
                  <p className="font-black text-sm">{drop.pageViews || 0}</p>
                  <p className="text-xs text-gray-500">Views</p>
                </div>
                <div className="bg-gray-100 p-2">
                  <BarChart3 size={16} className="mx-auto mb-1 text-gray-600" />
                  <p className="font-black text-sm">{drop.itemsSold || 0}</p>
                  <p className="text-xs text-gray-500">Sold</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(drop)}
                  className="flex-1 bg-black text-white py-2 font-bold uppercase text-sm flex items-center justify-center gap-1 hover:bg-gray-800"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
                {drop.isRaffle && drop.status === 'scheduled' && (
                  <button
                    onClick={() => handleDrawRaffle(drop._id)}
                    className="flex-1 bg-purple-600 text-white py-2 font-bold uppercase text-sm flex items-center justify-center gap-1 hover:bg-purple-700"
                  >
                    <Ticket size={14} />
                    Draw
                  </button>
                )}
                <button
                  onClick={() => handleDelete(drop._id)}
                  className="bg-red-600 text-white px-3 py-2 hover:bg-red-700"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border-4 border-black shadow-[8px_8px_0px_black]">
            <div className="bg-black text-white p-4 flex justify-between items-center">
              <h2 className="font-black text-xl uppercase">
                {selectedDrop ? 'Edit Drop' : 'Create Drop'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-white hover:text-yellow-400"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-sm mb-1">Drop Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">Slug *</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                    placeholder="drop-name"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-sm mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full p-2 border-2 border-black"
                  rows={2}
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-sm mb-1">Drop Date *</label>
                  <input
                    type="datetime-local"
                    value={formData.dropDate}
                    onChange={(e) => setFormData({...formData, dropDate: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">End Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                    className="w-full p-2 border-2 border-black"
                  />
                </div>
              </div>

              {/* Hero Image */}
              <div>
                <label className="block font-bold text-sm mb-1">Hero Image URL</label>
                <input
                  type="text"
                  value={formData.heroImage}
                  onChange={(e) => setFormData({...formData, heroImage: e.target.value})}
                  className="w-full p-2 border-2 border-black"
                  placeholder="https://..."
                />
              </div>

              {/* Settings */}
              <div className="border-2 border-gray-200 p-4 space-y-4">
                <h3 className="font-black uppercase">Drop Settings</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-bold text-sm mb-1">Total Quantity</label>
                    <input
                      type="number"
                      value={formData.totalQuantity}
                      onChange={(e) => setFormData({...formData, totalQuantity: parseInt(e.target.value)})}
                      className="w-full p-2 border-2 border-black"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-sm mb-1">Max Per Customer</label>
                    <input
                      type="number"
                      value={formData.maxPerCustomer}
                      onChange={(e) => setFormData({...formData, maxPerCustomer: parseInt(e.target.value)})}
                      className="w-full p-2 border-2 border-black"
                      min="1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isLimited}
                      onChange={(e) => setFormData({...formData, isLimited: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="font-bold text-sm">Limited Edition</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.useQueue}
                      onChange={(e) => setFormData({...formData, useQueue: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="font-bold text-sm">Use Queue System</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isRaffle}
                      onChange={(e) => setFormData({...formData, isRaffle: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="font-bold text-sm">Raffle Drop</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isCollab}
                      onChange={(e) => setFormData({...formData, isCollab: e.target.checked})}
                      className="w-5 h-5"
                    />
                    <span className="font-bold text-sm">Collaboration</span>
                  </label>
                </div>

                {formData.isCollab && (
                  <div>
                    <label className="block font-bold text-sm mb-1">Collaborator Name</label>
                    <input
                      type="text"
                      value={formData.collaboratorName}
                      onChange={(e) => setFormData({...formData, collaboratorName: e.target.value})}
                      className="w-full p-2 border-2 border-black"
                      placeholder="Brand/Artist name"
                    />
                  </div>
                )}
              </div>

              {/* Stats Panel (Edit Mode) */}
              {stats && (
                <div className="bg-yellow-50 border-4 border-yellow-400 p-4">
                  <h3 className="font-black uppercase mb-3">Drop Stats</h3>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-white p-2 border-2 border-black">
                      <p className="text-2xl font-black">{stats.pageViews}</p>
                      <p className="text-xs">Views</p>
                    </div>
                    <div className="bg-white p-2 border-2 border-black">
                      <p className="text-2xl font-black">{stats.notifyListCount}</p>
                      <p className="text-xs">Waiting</p>
                    </div>
                    <div className="bg-white p-2 border-2 border-black">
                      <p className="text-2xl font-black">{stats.itemsSold}</p>
                      <p className="text-xs">Sold</p>
                    </div>
                    <div className="bg-white p-2 border-2 border-black">
                      <p className="text-2xl font-black">{stats.sellThroughRate}%</p>
                      <p className="text-xs">Sell-Through</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 bg-gray-200 text-black py-3 font-bold uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-yellow-400 text-black py-3 font-black uppercase tracking-wider border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none transition-all disabled:opacity-50"
                >
                  {loading ? 'Saving...' : (selectedDrop ? 'Update Drop' : 'Create Drop')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropManagement;
